import { BlockBetween, BlockHead, BlockHeadContent, BlockTitle, Icon } from "components/Component";
import { MessagesContext } from "contexts/messagesContext";
import { DefaultKeyLeadsFilter } from "data/DefaultLeadFilters";
import Content from "layout/content/Content";
import Head from "layout/head/Head";
import React, { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { serviceExportLeads, serviceGetTicketLeads, serviceGetUserLeads } from "services/leads-monitoring";
import { serviceGetTicketCountByDepartment } from "services/tickets";
import LeadsMonitoringBody from "./LeadsMonitoringBody";
import LeadsMonitoringFilter from "./LeadsMonitoringFilter";
import { getPermission } from "utils/Utils";

const LeadsMonitoring = () => {
  const [kanbanFilter] = useState(DefaultKeyLeadsFilter);
  const [, setLeadsFilter] = useState(DefaultKeyLeadsFilter);
  const [smBtn, setSmBtn] = useState(false);
  const [cookies] = useCookies(["user"]);
  const [loading, setLoading] = useState(false);
  const [departmentsFilter, setDepartmentsFilter] = useState(cookies.departmentId);
  const [selectedFilterOptions, setSelectedFilterOptions] = useState([]);
  const [changeOption, setChangeOption] = useState(1);
  const [selectedSort, setSelectedSort] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  let roleFunctions = getPermission()["lead-monitorings"]?.access || [];
  const { messagesValue, salesUpdate } = useContext(MessagesContext);
  const [newMessages] = messagesValue;
  const [newSales] = salesUpdate;

  // Viewers without "Access_Own" (i.e. selain sales — CS / super-admin) see the whole
  // sales list and their leads at once instead of a single selected user.
  const showAllUsers = !roleFunctions.includes("Access_Own");

  // Full list of users (for the selector dropdown)
  const [allUsers, setAllUsers] = useState([]);
  // Single selected user with their tickets
  const [selectedUser, setSelectedUser] = useState(null);
  // All users with their tickets (used when showAllUsers is true)
  const [allUsersWithTickets, setAllUsersWithTickets] = useState([]);
  const [loadMoreId, setLoadMoreId] = useState("");
  const [ticketMetaData, setTicketMetaData] = useState({});

  // filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterUser, setFilterUser] = useState([]);
  const [customerPhoneSearch, setCustomerPhoneSearch] = useState("");

  const [departmentTicketCount, setDepartmentTicketCount] = useState(0);
  const [lastInteractionRange, setLastInteractionRange] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const refreshDepartmentCount = async () => {
    const resp = await serviceGetTicketCountByDepartment(cookies.SIDikey, departmentsFilter, filterStatus || undefined);
    if (resp?.status === 200 && Array.isArray(resp.data)) {
      const total = resp.data.reduce((sum, d) => sum + (d.count || 0), 0);
      setDepartmentTicketCount(total);
    }
  };

  // Fetch only the user list (no tickets yet)
  const refreshUserList = async () => {
    setLoading(true);

    const userResp = await serviceGetUserLeads(
      cookies.SIDikey,
      roleFunctions,
      departmentsFilter,
      1,
      50,
      "",
      cookies.id,
    );

    if (userResp.status === 200) {
      const users = userResp.data.data ?? [userResp.data];
      setAllUsers(users);

      if (showAllUsers) {
        // Selain sales: show every user and their leads at once.
        await refreshTicketsForAllUsers(users);
      } else {
        const currentSelected = selectedUser;
        const userInDept = currentSelected && users.find((u) => u.id === currentSelected.id);

        if (userInDept) {
          // Current user exists in this department — refresh their tickets with the new departmentId
          await refreshTicketsForUser(currentSelected);
        } else {
          // Current user not in this department (or no user selected) — auto-select first CS user
          const csUser = users.find((u) => u.role?.name === "CS") || users[0] || null;
          if (csUser) {
            await refreshTicketsForUser(csUser);
          } else {
            setSelectedUser(null);
          }
        }
      }
    }

    setLoading(false);
  };

  // Fetch first page of tickets for every user in parallel (showAllUsers mode)
  const refreshTicketsForAllUsers = async (users) => {
    if (!users || users.length === 0) {
      setAllUsersWithTickets([]);
      return;
    }
    setLoading(true);

    const results = await Promise.all(
      users.map(async (user) => {
        const respTicket = await serviceGetTicketLeads(
          cookies.SIDikey,
          ["Access_Own"],
          departmentsFilter,
          user.id,
          1,
          filterStatus,
          selectedSort,
          "DESC",
          customerPhoneSearch,
          lastInteractionRange,
        );

        if (respTicket.status === 200) {
          return {
            ...user,
            tickets: respTicket.data.data,
            ticketCount: respTicket.data.meta.itemCount,
            metaData: respTicket.data.meta,
          };
        }

        return { ...user, tickets: [], ticketCount: 0, metaData: {} };
      }),
    );

    setAllUsersWithTickets(results);
    setLoading(false);
  };

  // Fetch tickets for a specific user and set as selectedUser
  const refreshTicketsForUser = async (user) => {
    if (!user) return;
    setLoading(true);

    const respTicket = await serviceGetTicketLeads(
      cookies.SIDikey,
      ["Access_Own"],
      departmentsFilter,
      user.id,
      1,
      filterStatus,
      selectedSort,
      "DESC",
      customerPhoneSearch,
    );

    if (respTicket.status === 200) {
      setSelectedUser({
        ...user,
        tickets: respTicket.data.data,
        ticketCount: respTicket.data.meta.itemCount,
        metaData: respTicket.data.meta,
      });
    }

    setLoading(false);
  };

  const loadMoreTickets = async (userId) => {
    if (showAllUsers) {
      const targetUser = allUsersWithTickets.find((u) => u.id === userId);
      if (!targetUser) return;

      const respTicket = await serviceGetTicketLeads(
        cookies.SIDikey,
        ["Access_Own"],
        departmentsFilter,
        userId,
        (ticketMetaData.page || 1) + 1,
        filterStatus,
        selectedSort,
        "DESC",
        customerPhoneSearch,
      );

      if (respTicket.status === 200) {
        setAllUsersWithTickets((prev) =>
          prev.map((u) =>
            u.id === userId
              ? {
                  ...u,
                  tickets: [...(u.tickets || []), ...respTicket.data.data],
                  ticketCount: respTicket.data.meta.itemCount,
                  metaData: respTicket.data.meta,
                }
              : u,
          ),
        );
      }
      return;
    }

    if (!selectedUser || selectedUser.id !== userId) return;

    const respTicket = await serviceGetTicketLeads(
      cookies.SIDikey,
      ["Access_Own"],
      departmentsFilter,
      userId,
      (ticketMetaData.page || 1) + 1,
      filterStatus,
      selectedSort,
      "DESC",
      customerPhoneSearch,
    );

    if (respTicket.status === 200) {
      setSelectedUser((prev) => ({
        ...prev,
        tickets: [...(prev.tickets || []), ...respTicket.data.data],
        ticketCount: respTicket.data.meta.itemCount,
        metaData: respTicket.data.meta,
      }));
    }
  };

  const handleSelectUser = async (user) => {
    await refreshTicketsForUser(user);
  };

  const handleLastInteractionFilter = (range) => {
    setLastInteractionRange(range);
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const userId = showAllUsers ? undefined : selectedUser?.id;
      const resp = await serviceExportLeads(
        cookies.SIDikey,
        departmentsFilter,
        userId,
        filterStatus,
        selectedSort,
        "DESC",
        customerPhoneSearch,
        lastInteractionRange,
      );

      if (resp.status === 200 && resp.data) {
        const disposition = resp.headers?.["content-disposition"] ?? "";
        const match = disposition.match(/filename[^;=\n]*=([^;\n]*)/);
        const filename = match
          ? match[1].trim().replaceAll('"', "")
          : `LeadsMonitoring_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;

        const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv;charset=utf-8;" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      // silently ignore download errors
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    refreshUserList();
    refreshDepartmentCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentsFilter]);

  useEffect(() => {
    refreshDepartmentCount();
    if (showAllUsers) {
      refreshTicketsForAllUsers(allUsers);
    } else if (selectedUser) {
      refreshTicketsForUser(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  useEffect(() => {
    if (showAllUsers) {
      refreshTicketsForAllUsers(allUsers);
    } else if (selectedUser) {
      refreshTicketsForUser(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPhoneSearch]);

  useEffect(() => {
    if (showAllUsers) {
      refreshTicketsForAllUsers(allUsers);
    } else if (selectedUser) {
      refreshTicketsForUser(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSort]);

  useEffect(() => {
    if (showAllUsers) {
      refreshTicketsForAllUsers(allUsers);
    } else if (selectedUser) {
      refreshTicketsForUser(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastInteractionRange]);

  useEffect(() => {
    loadMoreTickets(loadMoreId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreId, ticketMetaData]);

  useEffect(() => {
    // refresh tickets if there's new messages
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMessages]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSales]);

  const handleDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <React.Fragment>
      <Head title="Leads Monitoring"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Leads Monitoring</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#toggle"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmBtn(!smBtn);
                  }}
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                >
                  <Icon name="menu-alt-r"></Icon>
                </a>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <LeadsMonitoringFilter
          setLeadsFilter={setLeadsFilter}
          departmentsFilter={departmentsFilter}
          setDepartmentsFilter={setDepartmentsFilter}
          cookies={cookies}
          selectedFilterOptions={selectedFilterOptions}
          setSelectedFilterOptions={setSelectedFilterOptions}
          changeOption={changeOption}
          setChangeOption={setChangeOption}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
          handleDate={handleDate}
          startDate={startDate}
          endDate={endDate}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterUser={filterUser}
          setFilterUser={setFilterUser}
          loading={loading}
          allUsers={allUsers}
          selectedUser={selectedUser}
          onSelectUser={handleSelectUser}
          showAllUsers={showAllUsers}
          customerPhoneSearch={customerPhoneSearch}
          onSearchCustomerPhone={setCustomerPhoneSearch}
          lastInteractionRange={lastInteractionRange}
          onLastInteractionFilter={handleLastInteractionFilter}
          onExportCsv={handleExportCsv}
          isExporting={isExporting}
        />

        <LeadsMonitoringBody
          selectedUser={selectedUser}
          showAllUsers={showAllUsers}
          allUsersWithTickets={allUsersWithTickets}
          loading={loading}
          kanbanFilter={kanbanFilter}
          setLoadMoreId={setLoadMoreId}
          setTicketMetaData={setTicketMetaData}
          selectedSort={selectedSort}
          departmentTicketCount={departmentTicketCount}
        />
      </Content>
    </React.Fragment>
  );
};

export default LeadsMonitoring;
